<!--
  PROJECT SELECTION — a screen, not a dialog.

  Joel: project-dependent tabs "should not be available until a project is
  selected". Selection is therefore the landing route, and the modal geometry that
  used to float this over whichever cure screen happened to be behind it — with its
  own minimize, maximize, drag and eight resize handles — is gone. A picker you must
  dismiss to see what you picked was always the wrong shape for the first thing a
  user does.
-->
<div class="project-selection">
  <header class="selection-header">
    <h1>{{ 'ProjectSelectionSelectAProject' | t }}</h1>
    <button type="button" class="upload-link-btn" (click)="openUpload()">{{ 'ProjectSelectionUploadAProject' | t }}</button>
    <!-- The accepted types are the upload pipeline's own, not a guess: the same
         ".zip,application/zip" the existing uploader has always declared. -->
    <input
      #projectUploadInput
      type="file"
      accept=".zip,application/zip"
      hidden
      (change)="onUploadFileChosen($event)" />
    <p class="selection-hint" *ngIf="returnTo">{{ 'ProjectSelectionChooseAProjectToContinueTo' | t }}</p>
  </header>

<!-- The "Default Settings for New Projects / Units" panel stood here. Units are
     a REGIONAL SETTING now, on the preferences screen, resolved through the
     global -> company -> user cascade like language and date format. Leaving the
     radio here would have given one setting two homes that disagree: this one
     wrote a session field, the preference is stored per user, and whichever the
     reader touched last would win with nothing on screen to say so. -->

<!-- Search and Filter Controls -->
<div class="search-filter-container">
  <!-- Search Bar -->
  <div class="search-bar">
    <div class="search-input-container">
      <input 
        type="text" 
        class="search-input" 
        [placeholder]="'ProjectSelectionSearchProjectsByNameSerialNumber' | t"
        [(ngModel)]="searchTerm"
        (ngModelChange)="onCriteriaChanged()"
       >
      <button 
        class="clear-search-btn" 
        *ngIf="searchTerm" 
        (click)="clearSearch()"
        [title]="'ProjectSelectionClearSearch' | t">
        ×
      </button>
    </div>
  </div>
  
  <!-- Filter Controls -->
  <div class="filter-controls">
    <div class="basic-filters">
      <select class="filter-select" [(ngModel)]="selectedFilter" (ngModelChange)="onCriteriaChanged()">
        <option value="all">All Projects ({{ gridModel.counts.total }})</option>
        <option value="live">Live Projects ({{ gridModel.counts.live }})</option>
        <option value="historical">Historical Projects ({{ gridModel.counts.historical }})</option>
      </select>
      
      <button 
        class="advanced-filter-toggle" 
        (click)="toggleAdvancedFilters()"
        [class.active]="showAdvancedFilters">
        <span>{{ 'ProjectSelectionAdvancedFilters' | t }}</span>
        <span class="toggle-icon" [class.rotated]="showAdvancedFilters">▼</span>
      </button>
      
      <button class="clear-filters-btn" (click)="clearFilters()" [title]="'ProjectSelectionClearAllFilters' | t">{{ 'ProjectSelectionClearAll' | t }}</button>

      <button
        class="advanced-filter-toggle"
        (click)="toggleColumnChooser()"
        [class.active]="showColumnChooser">
        <span>{{ 'ProjectSelectionColumnsVisibleOfTotal' | t: { visible: visibleColumnCount, total: selectableColumnCount } }}</span>
        <span class="toggle-icon" [class.rotated]="showColumnChooser">▼</span>
      </button>
    </div>
    
    <!-- Advanced Filters -->
    <div class="advanced-filters" *ngIf="showAdvancedFilters">
      <div class="date-filters">
        <div class="date-filter-group">
          <label>{{ 'ProjectSelectionFromDate' | t }}</label>
          <input 
            type="date" 
            class="date-input" 
            [(ngModel)]="dateFilterFrom"
            (ngModelChange)="onCriteriaChanged()">
        </div>
        <div class="date-filter-group">
          <label>{{ 'ProjectSelectionToDate' | t }}</label>
          <input 
            type="date" 
            class="date-input" 
            [(ngModel)]="dateFilterTo"
            (ngModelChange)="onCriteriaChanged()">
        </div>
      </div>
    </div>
  </div>
  

    <!-- COLUMN CHOOSER. Visibility, order and grouping in one place, because they are
         one decision: a column you cannot see cannot be sorted, grouped or filtered by,
         and splitting them across three menus is how a user ends up grouping by
         something invisible. -->
    <div class="column-chooser" *ngIf="showColumnChooser">
      <!-- SAVE TARGETS. A tier the user cannot write is omitted rather than greyed:
           a disabled "Save as global default" teaches a technician only that there
           is a power they will never have. The exception is a user tier the company
           has LOCKED, which is shown with its reason — the user could save
           yesterday, so the explanation is the point. -->
      <div class="save-bar">
        <button
          type="button"
          class="save-btn"
          *ngFor="let target of saveTargets"
          [disabled]="!!target.disabledReason"
          [attr.title]="target.disabledReason"
          (click)="saveView(target.tier)">
          {{ target.label }}
        </button>

        <button
          type="button"
          class="reset-btn"
          *ngIf="canReset(defaultSaveTier)"
          (click)="resetView(defaultSaveTier)">
          {{ resetLabelFor(defaultSaveTier) }}
        </button>

        <span class="save-status" *ngIf="saveStatus" role="status">{{ saveStatus }}</span>
      </div>

      <div class="chooser-hint">
        Showing {{ visibleColumnCount }} columns. Group by up to {{ maxGroupLevels }}.
      </div>
      <ul class="chooser-list">
        <li class="chooser-item" *ngFor="let column of columnCatalogue">
          <label class="chooser-label">
            <input
              type="checkbox"
              [checked]="isColumnVisible(column.id)"
              [disabled]="column.id === requiredColumn"
              [attr.title]="column.id === requiredColumn ? 'The project name is always shown' : null"
              (change)="onToggleColumn(column.id)" />
            <span>{{ column.label }}</span>
          </label>

          <span class="chooser-actions" *ngIf="isColumnVisible(column.id)">
            <button
              type="button"
              class="chooser-btn"
              [title]="'ProjectSelectionMoveLeft' | t"
              [disabled]="columnPosition(column.id) <= 0"
              (click)="onMoveColumn(column.id, -1)">◀</button>
            <button
              type="button"
              class="chooser-btn"
              [title]="'ProjectSelectionMoveRight' | t"
              [disabled]="columnPosition(column.id) >= visibleColumnCount - 1"
              (click)="onMoveColumn(column.id, 1)">▶</button>
            <button
              type="button"
              class="chooser-btn"
              [class.active]="isGrouped(column.id)"
              [title]="isGrouped(column.id) ? 'Stop grouping by this column' : 'Group by this column'"
              (click)="onToggleGroupBy(column.id)">⊞</button>
          </span>
        </li>
      </ul>
    </div>

  <!-- Results Summary -->
  <div class="results-summary" *ngIf="searchTerm || selectedFilter !== 'all' || dateFilterFrom || dateFilterTo">
    <span class="results-text">
      Showing {{ gridModel.counts.filtered }} of {{ gridModel.counts.total }} projects
    </span>
  </div>
</div>

<app-project-grid
  [model]="gridModel"
  [view]="gridView"
  (sortToggle)="onSortToggle($event)"
  (filterChange)="onFilterChange($event)"
  [showShareLink]="true"
  [shareLinkCopiedFor]="shareLinkCopiedFor"
  (selectProject)="onSelectProject($event)"
  (copyLink)="copyProjectLink($event.project, $event.event)">
</app-project-grid>

<div class="project-table-wrapper">
  <!-- COULD NOT LOAD is not the same as HAS NONE, and they used to render
       identically. The failure branch comes first: filters are irrelevant
       when the list never arrived, so offering "Clear Filters" would send
       the user to adjust something that is not the problem. -->
  <div *ngIf="projectLoad.kind === 'failed'" class="no-results load-failed">
    <div class="no-results-icon">⚠️</div>
    <div class="no-results-text">{{ projectLoad.message }}</div>
    <div class="load-failed-detail" *ngIf="projectLoad.detail">{{ projectLoad.detail }}</div>
    <!-- THE VERB MUST MATCH THE FAULT. "Try again" cannot fix an expired token;
         it just fails identically. Transport failures keep it, because retrying
         IS the right move there. Carried over from main-layout with the markup —
         the picker moved to this screen, and the distinction moved with it. -->
    <button
      *ngIf="!projectLoad.authRequired"
      class="clear-filters-btn"
      (click)="retryProjectLoad()">{{ 'ProjectSelectionTryAgain' | t }}</button>
    <button
      *ngIf="projectLoad.authRequired"
      class="clear-filters-btn"
      (click)="signInAgain()">{{ 'ProjectSelectionSignIn' | t }}</button>
  </div>

  <!-- No Results Message -->
  <div *ngIf="projectLoad.kind !== 'failed' && gridModel.rows.length === 0" class="no-results">
    <div class="no-results-icon">🔍</div>
    <div class="no-results-text">{{ 'ProjectSelectionNoProjectsFoundMatchingYourCriteria' | t }}</div>
    <button class="clear-filters-btn" (click)="clearFilters()">{{ 'ProjectSelectionClearFilters' | t }}</button>
  </div>
</div>
